import styled, { css } from 'styled-components';
import { prsButtonTheme } from '../themes/prsButtonThemes';

// Shape + behavior shared across all sizes of .prs-btn (Prose v1.5).
// Size-specific bases below apply only padding + the matching button typography
// tokens. Drops sm/square/circle/icon and the success color variants — re-add
// as separate components if needed.
const prsBtnBase = css`
  border-width: var(--prs-border-btn);
  border-style: solid;
  border-color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
  text-decoration-line: none;
  border-radius: var(--prs-radius-btn);
  transition-property: var(--prs-transition-property);
  transition-timing-function: var(--prs-transition-timing);
  transition-duration: var(--prs-transition-duration);

  &:focus-visible {
    outline: 2px solid transparent;
    outline-offset: 1px;
  }

  &:disabled {
    background-color: var(--prs-c-gray-400);
    color: var(--prs-c-white);
    cursor: not-allowed;
  }
`;

const prsBtnLgBase = css`
  ${prsBtnBase}
  padding: 0.75rem 1rem;
  font-weight: var(--prs-fw-button-lg);
  font-size: var(--prs-fz-button-lg);
  line-height: var(--prs-fl-button-lg);
`;

const prsBtnMdBase = css`
  ${prsBtnBase}
  padding: 0.25rem 1rem;
  font-weight: var(--prs-fw-button-md);
  font-size: var(--prs-fz-button-md);
  line-height: var(--prs-fl-button-md);
`;

export const PrsButton = styled.button`
  cursor: pointer;
  text-align: center;
  vertical-align: middle;
  outline-offset: 2px;
  user-select: none;
  padding-inline: ${prsButtonTheme.regular.padding};
  color: ${prsButtonTheme.regular.color};
  --tw-prose-links: ${prsButtonTheme.regular.color};
  height: ${prsButtonTheme.regular.height};
  font-family: ${prsButtonTheme.regular.fontFamily};
  font-size: ${prsButtonTheme.regular.fontSize};
  outline-color: ${prsButtonTheme.regular.baseContentColor};
  background-color: ${prsButtonTheme.regular.backgroundColor};
  background-size: auto,calc(${prsButtonTheme.regular.noiseSize}*100%);
  background-image: ${prsButtonTheme.regular.backgroundImage};
  border-width: ${prsButtonTheme.regular.borderWidth};
  border-style: solid;
  border-color: ${prsButtonTheme.regular.borderColor};
  text-shadow: 0 .5px oklch(100% 0 0/calc(${prsButtonTheme.regular.depth}*.15));
  touch-action: manipulation;
  box-shadow: 0 .5px 0 .5px oklch(100% 0 0/calc(${prsButtonTheme.regular.depth}*6%))inset,${prsButtonTheme.regular.buttonShadow};
  border-start-start-radius: ${prsButtonTheme.regular.borderRadius};
  border-start-end-radius: ${prsButtonTheme.regular.borderRadius};
  border-end-end-radius: ${prsButtonTheme.regular.borderRadius};
  border-end-start-radius: ${prsButtonTheme.regular.borderRadius};
  flex-wrap: nowrap;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  gap: 0.375rem;
  font-weight: 600;
  transition-property: color, background-color, border-color, box-shadow;
  transition-duration: 0.2s;
  transition-timing-function: cubic-bezier(0, 0, 0.2, 1);
  display: inline-flex;
  :hover {
    background-color: ${prsButtonTheme.regular.hoverBackgroundColor};
    border-color: ${prsButtonTheme.regular.hoverBorderColor};
    color: ${prsButtonTheme.regular.hoverColor};
  }
  :focus-visible {
    border-color: ${prsButtonTheme.focused.focusedBorderColor};
    background-color: ${prsButtonTheme.focused.focusedBackgroundColor};
    color: ${prsButtonTheme.focused.focusedColor};
    outline: ${prsButtonTheme.focused.focusedOutline};
    outline-offset: ${prsButtonTheme.focused.focusedOutlineOffset};
  }
`;

export const PrsDefaultButton = styled(PrsButton)`
  background-color: ${prsButtonTheme.default.backgroundColor};
  border-color: ${prsButtonTheme.default.borderColor};
  color: ${prsButtonTheme.default.color};
  :hover {
    background-color: ${prsButtonTheme.default.hoverBackgroundColor};
    border-color: ${prsButtonTheme.default.hoverBorderColor};
    color: ${prsButtonTheme.default.hoverColor};
  }
  :disabled {
    background-color: ${prsButtonTheme.default.disabledBackgroundColor};
    border-color: ${prsButtonTheme.default.disabledBorderColor};
    color: ${prsButtonTheme.default.disabledColor};
  }
  :focus-visible {
    border-color: ${prsButtonTheme.focused.focusedBorderColor};
    background-color: ${prsButtonTheme.focused.focusedBackgroundColor};
    color: ${prsButtonTheme.focused.focusedColor};
    outline: ${prsButtonTheme.focused.focusedOutline};
    outline-offset: ${prsButtonTheme.focused.focusedOutlineOffset};
  }
`;

// .prs-btn-primary @ .prs-btn-lg
export const PrsButtonLg = styled.button`
  ${prsBtnLgBase}
  background-color: var(--prs-c-primary);
  color: var(--prs-c-white);

  &:hover {
    background-color: var(--prs-c-primary-900);
  }

  &:focus-visible {
    background-color: var(--prs-c-primary);
    outline-color: var(--prs-c-primary);
  }

  &:disabled {
    background-color: var(--prs-c-gray-400);
    color: var(--prs-c-white);
  }
`;

export const PrsButtonStackLg = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  flex: 1 0 0;
  margin-top: 2rem;
`;

// .prs-btn-secondary @ .prs-btn-lg
export const PrsOutlineButtonLg = styled.button`
  ${prsBtnLgBase}
  border-color: currentColor;
  background-color: transparent;
  color: var(--prs-c-primary);

  &:hover {
    border-color: var(--prs-c-primary);
    background-color: var(--prs-c-primary-100);
    color: var(--prs-c-primary);
  }

  &:focus-visible {
    background-color: inherit;
    outline-color: currentColor;
    color: var(--prs-c-primary);
  }

  &:disabled,
  &:disabled:hover {
    border-color: currentColor;
    background-color: transparent;
    color: var(--prs-c-gray-500);
  }
`;

// .prs-btn-secondary @ .prs-btn-md
export const PrsOutlineButtonMd = styled.button`
  ${prsBtnMdBase}
  border-color: var(--prs-c-primary);
  background-color: var(--prs-c-white);
  color: var(--prs-c-primary);

  &:hover {
    background-color: var(--prs-c-primary-100);
  }

  &:focus-visible {
    outline-color: var(--prs-c-primary);
  }

  &:disabled,
  &:disabled:hover {
    border-color: var(--prs-c-gray-400);
    background-color: var(--prs-c-white);
    color: var(--prs-c-gray-500);
  }
`;

// .prs-btn-tertiary @ .prs-btn-lg
export const PrsTertiaryButtonLg = styled.button`
  ${prsBtnLgBase}
  background-color: transparent;
  color: var(--prs-c-primary);

  &:hover {
    border-color: currentColor;
    color: var(--prs-c-primary);
    text-decoration: underline;
  }

  &:focus-visible {
    border-color: transparent;
    background-color: inherit;
    text-decoration: none;
    outline-color: currentColor;
  }

  &:disabled,
  &:disabled:hover {
    border-color: transparent;
    background-color: transparent;
    color: var(--prs-c-gray-500);
    text-decoration: none;
  }
`;

// .prs-btn-danger @ .prs-btn-md
export const PrsDangerButtonMd = styled.button`
  ${prsBtnMdBase}
  background-color: var(--prs-c-danger);
  color: var(--prs-c-white);

  &:hover {
    background-color: var(--prs-c-danger-700);
  }

  &:focus-visible {
    outline-color: var(--prs-c-danger);
  }
`;
